import React, { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, File as FileIcon, Eye, Globe, Users, Lock } from "lucide-react";

const VISIBILITY = [
  { key: "public", label: "Öffentlich", icon: Globe, color: "bg-emerald-100 text-emerald-800" },
  { key: "customer", label: "Nur Kunden", icon: Users, color: "bg-blue-100 text-blue-800" },
  { key: "admin", label: "Nur Admin", icon: Lock, color: "bg-slate-200 text-slate-700" },
];

export default function AdminDownloads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", visibility: "public", category: "" });
  const fileRef = useRef(null);

  const load = () => api.get("/admin/downloads").then(({ data }) => { setItems(data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Bitte Datei auswählen"); return; }
    if (!form.title.trim()) { toast.error("Titel erforderlich"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("visibility", form.visibility);
    fd.append("category", form.category);
    setUploading(true);
    try {
      await api.post("/admin/downloads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Hochgeladen");
      setForm({ title: "", description: "", visibility: "public", category: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error("Upload fehlgeschlagen");
    } finally { setUploading(false); }
  };

  const updateVis = async (id, visibility) => {
    try {
      const it = items.find((x) => x.id === id);
      await api.patch(`/admin/downloads/${id}`, { title: it.title, description: it.description, visibility, category: it.category });
      toast.success("Aktualisiert");
      load();
    } catch { toast.error("Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Datei wirklich löschen?")) return;
    try {
      await api.delete(`/admin/downloads/${id}`);
      toast.success("Gelöscht");
      load();
    } catch { toast.error("Fehler"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="admin-downloads">
      <h1 className="font-heading text-3xl font-bold text-[#1A2433] mb-2">Downloads</h1>
      <p className="text-slate-600 mb-6">PDFs, Formulare und Merkblätter für Besucher/Kunden bereitstellen.</p>

      <form onSubmit={upload} className="border-2 border-dashed border-slate-300 rounded-2xl p-6 mb-8 bg-slate-50" data-testid="upload-form">
        <h2 className="font-heading font-semibold text-[#1A2433] mb-4 flex items-center gap-2">
          <Upload className="w-4 h-4 text-[#991B1B]" /> Neue Datei hochladen
        </h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <input
            type="text" required placeholder="Titel *" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
            data-testid="upload-title"
          />
          <input
            type="text" placeholder="Kategorie (optional)" value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
          />
        </div>
        <textarea
          rows={2} placeholder="Beschreibung (optional)" value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B] mb-3"
        />
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Sichtbarkeit</label>
            <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 bg-white">
              {VISIBILITY.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </div>
          <input ref={fileRef} type="file" required className="text-sm" data-testid="upload-file" />
          <button type="submit" disabled={uploading} className="btn-primary ml-auto" data-testid="upload-submit">
            <Upload className="w-4 h-4" /> {uploading ? "Lade hoch…" : "Hochladen"}
          </button>
        </div>
      </form>

      {loading ? <div className="text-slate-500">Lade…</div> : (
        <div className="space-y-3">
          {items.map((d) => {
            const v = VISIBILITY.find((x) => x.key === d.visibility) || VISIBILITY[0];
            const VIcon = v.icon;
            return (
              <div key={d.id} className="border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`download-row-${d.id}`}>
                <div className="w-11 h-11 rounded-lg bg-[#1A2433] flex items-center justify-center flex-shrink-0">
                  <FileIcon className="w-5 h-5 text-[#DC2626]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1A2433] truncate">{d.title}</div>
                  <div className="text-xs text-slate-500 truncate">{d.filename} · {(d.size_bytes / 1024).toFixed(1)} KB · {d.downloads_count || 0} Downloads</div>
                  {d.description && <div className="text-xs text-slate-500 mt-1 truncate">{d.description}</div>}
                </div>
                <select value={d.visibility} onChange={(e) => updateVis(d.id, e.target.value)}
                  className="px-2 py-1.5 rounded-md border border-slate-300 text-xs">
                  {VISIBILITY.map((opt) => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
                </select>
                <a href={`${API}/downloads/${d.id}/file`} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#991B1B] p-2">
                  <Eye className="w-4 h-4" />
                </a>
                <button onClick={() => del(d.id)} className="text-red-500 hover:text-red-600 p-2">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          {items.length === 0 && <div className="text-slate-500 text-center py-10">Noch keine Dateien hochgeladen.</div>}
        </div>
      )}
    </div>
  );
}
